/**
 * OAuth 콜백 처리 컴포넌트
 *
 * 이 컴포넌트는 소셜 로그인(카카오, 네이버, 구글 등) 인증 후
 * 리다이렉트되는 페이지에서 인증 코드를 처리하는 역할을 합니다.
 */

import React, { useEffect, useRef, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OAuthProvider, handleOAuthCallback } from "@/utils/oauthUtils";
import { clearTokens } from "@/utils/tokenUtils";
import ConfirmContext from "@/context/confirm/ConfirmContext";
import logger from "@/utils/logger";

interface OAuthCallbackProps {
    provider: OAuthProvider;
}

const OAuthCallback: React.FC<OAuthCallbackProps> = ({ provider }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCallbackProcessed = useRef(false);

    // 에러 팝업 상태 관리
    const { showPopup } = useContext(ConfirmContext);

    useEffect(() => {
        /**
         * OAuth 콜백 처리 함수
         */
        const processOAuthCallback = async () => {
            // 이미 처리된 경우 중복 실행 방지
            if (isCallbackProcessed.current) return;

            // URL에서 인증 코드 추출
            const code = searchParams.get("code");

            if (code) {
                isCallbackProcessed.current = true;

                try {
                    // OAuth 콜백 처리
                    const response = await handleOAuthCallback(provider, code);

                    // 로그인 결과 처리
                    if (response.data.isRegistered) {
                        // 저장된 리다이렉션 URL 확인
                        const redirectUrl = sessionStorage.getItem("redirectUrl");
                        // 리다이렉션 URL 삭제
                        sessionStorage.removeItem("redirectUrl");

                        // 저장된 URL이 있으면 해당 URL로, 없으면 홈으로 이동
                        if (redirectUrl) {
                            window.history.replaceState(null, "", redirectUrl);
                            navigate(redirectUrl, { replace: true });
                        } else {
                            window.history.replaceState(null, "", "/home");
                            navigate("/home", { replace: true });
                        }
                    } else {
                        // 미가입 사용자는 회원가입 동의 페이지로 이동
                        navigate("/join/agreement", { replace: true });
                    }
                } catch (error: any) {
                    // 에러 처리
                    clearTokens();

                    // 서버에서 보낸 에러 메시지 그대로 사용
                    const errorMessage =
                        error.message && error.message !== "Network Error"
                            ? error.message
                            : "로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.";

                    // 에러 팝업 표시
                    await showPopup(errorMessage, {
                        type: "alert"
                    }).then(() => {
                        navigate("/auth/login");
                    });
                }
            } else {
                // 인증 코드가 없는 경우
                logger.error("인가 코드가 없습니다.");
                await showPopup("인증 정보가 없습니다. 다시 로그인해주세요.", {
                    type: "alert"
                }).then(() => {
                    navigate("/auth/login");
                });
            }
        };

        processOAuthCallback();
    }, [navigate, searchParams, provider, showPopup]);

    return (
        <div className="flex justify-center items-center min-h-screen">
            <div>{provider} 로그인 처리 중...</div>
        </div>
    );
};

export default OAuthCallback;
